import React, { useEffect, useRef, useState } from 'react';
import {
  HiOutlineUser,
  HiOutlineDotsHorizontal,
  HiOutlineDuplicate,
  HiOutlineLogout,
  HiOutlineCheck,
} from 'react-icons/hi';

import { getUserId, resetUserId } from '../../helpers/utils/user-util';
import { startNewSession } from '../../helpers/utils/session-util';

/**
 * Profile chip at the foot of the sidebar.
 *
 * There is no login in this channel — every visitor is a guest — but they do
 * carry a durable `userId` cookie, and the agent keys its persistent memory on
 * it. That makes two actions meaningful here:
 *
 *   Copy user ID  the id is what a builder needs to look this person up on the
 *                 console's Memory page, so it has to be retrievable.
 *   Log out       mint a new id, so the next turn starts from no memory. This
 *                 is the visitor's "forget me": it does not delete what is
 *                 stored server-side, it stops this browser identifying as
 *                 that person.
 *
 * Logging out is confirmed first: it is effectively irreversible from here,
 * since the old id is not shown again once replaced.
 */
export default function UserProfile({ collapsed, onLoggedOut }) {
  const [userId, setUserId] = useState(() => getUserId());
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [copied, setCopied] = useState(false);
  const menuRef = useRef(null);

  // Close the menu on an outside click or Escape — a bare dropdown that only
  // closes on re-click feels stuck.
  useEffect(() => {
    if (!menuOpen) return undefined;

    const onDocClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };

    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  // Reset the "Copied" tick so the button does not stay stuck on confirmation.
  useEffect(() => {
    if (!copied) return undefined;
    const timer = setTimeout(() => setCopied(false), 1600);
    return () => clearTimeout(timer);
  }, [copied]);

  const handleCopy = async () => {
    const id = getUserId();
    try {
      await navigator.clipboard.writeText(id);
      setCopied(true);
    } catch (e) {
      // clipboard is unavailable over plain http and in some embedded frames;
      // fall back to a selectable prompt rather than failing silently.
      // eslint-disable-next-line no-alert
      window.prompt('Copy your user ID:', id);
    }
    setMenuOpen(false);
  };

  const handleLogout = () => {
    // A new identity, and a new conversation to go with it: keeping the old
    // session would leave the previous person's transcript on screen under a
    // new id.
    const next = resetUserId();
    startNewSession();
    setUserId(next);
    setConfirming(false);
    setMenuOpen(false);
    if (onLoggedOut) {
      onLoggedOut(next);
    } else {
      // No handler wired: reload so every component picks up the new identity
      // rather than holding the old one in state.
      window.location.reload();
    }
  };

  const shortId = userId ? `${userId.slice(0, 10)}…` : '';

  return (
    <>
      <div
        className={`relative flex items-center gap-2.5 border-t border-hairline px-3 py-3 ${
          collapsed ? 'justify-center' : ''
        }`}
      >
        <span
          className="inline-flex h-7 w-7 flex-none items-center justify-center rounded-full
            bg-elevated text-ink-muted"
          aria-hidden="true"
        >
          <HiOutlineUser size={15} />
        </span>

        {!collapsed && (
          <>
            <div className="min-w-0 flex-1">
              <p className="truncate text-[13px] font-medium text-ink">Guest</p>
              <p className="truncate font-mono text-[11px] text-ink-faint" title={userId}>
                {shortId}
              </p>
            </div>

            <div ref={menuRef} className="relative flex-none">
              <button
                type="button"
                onClick={() => setMenuOpen((open) => !open)}
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                aria-label="Account options"
                className="inline-flex h-7 w-7 items-center justify-center rounded-md
                  text-ink-muted transition-colors hover:bg-elevated hover:text-ink"
              >
                <HiOutlineDotsHorizontal size={16} />
              </button>

              {menuOpen && (
                <div
                  role="menu"
                  className="absolute bottom-9 right-0 z-40 w-52 overflow-hidden rounded-lg
                    border border-hairline bg-surface py-1 shadow-lg"
                >
                  <button
                    type="button"
                    role="menuitem"
                    onClick={handleCopy}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-[13px]
                      text-ink transition-colors hover:bg-elevated"
                  >
                    {copied ? (
                      <HiOutlineCheck size={15} className="flex-none" />
                    ) : (
                      <HiOutlineDuplicate size={15} className="flex-none" />
                    )}
                    {copied ? 'Copied' : 'Copy user ID'}
                  </button>

                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setMenuOpen(false);
                      setConfirming(true);
                    }}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-[13px]
                      text-ink transition-colors hover:bg-elevated"
                  >
                    <HiOutlineLogout size={15} className="flex-none" />
                    Log out
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {confirming && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="logout-title"
          onClick={(e) => {
            if (e.target === e.currentTarget) setConfirming(false);
          }}
        >
          <div className="w-full max-w-sm rounded-xl border border-hairline bg-surface p-5 shadow-xl">
            <h2 id="logout-title" className="text-[15px] font-semibold text-ink">
              Log out of this device?
            </h2>
            <p className="mt-2 text-[13px] leading-5 text-ink-muted">
              You will start over as a new visitor. The assistant will no longer
              remember anything it learned about you, and this conversation will
              be replaced by a new one.
            </p>

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirming(false)}
                className="rounded-lg border border-hairline px-3 py-1.5 text-[13px]
                  text-ink transition-colors hover:bg-elevated"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleLogout}
                className="rounded-lg bg-red-600 px-3 py-1.5 text-[13px] font-medium
                  text-white transition-colors hover:bg-red-700"
              >
                Log out
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
